<template>
  <div
    class="w-full"
    :class="[{ 'mx-auto max-w-lg': !embedded }, pageFlowClass]"
    :data-testid="dataTestid"
  >
    <div
      v-if="currentStep === STEP_SUCCESS"
      class="text-center"
      :class="[stepFlowClass, bottomSpacingClass]">
      <div class="mb-6 flex justify-center">
        <img
          src="/images/auth/mfa-enrollment-success.png"
          alt=""
          width="160"
          height="160"
        />
      </div>
      <h1 class="mb-2 text-lg font-bold leading-[150%] text-semantic-surface-on-surface">
        You're all set
      </h1>
      <p class="mb-2 text-base font-normal leading-[150%] text-semantic-surface-on-surface">
        {{ successBody }}
      </p>
      <!-- Icon matches the number's line height (text-lg, 150%), per design. -->
      <p class="mb-6 flex items-center justify-center gap-2 text-lg font-bold leading-[150%] text-semantic-surface-on-surface">
        <V2IconsPhoneOutgoingOutline
          width="24"
          height="24"
          class="shrink-0"
        />
        {{ formattedCompletedPhone }}
      </p>
      <p
        v-if="!isRecovery"
        class="mb-6 text-sm font-normal leading-[150%] text-semantic-surface-on-surface-variant">
        Update your number or turn verification off any time in
        <button
          id="mfa_enrollment_go_to_profile_btn"
          type="button"
          data-testid="mfa-enrollment-go-to-profile-btn"
          class="font-bold text-semantic-surface-on-surface-variant underline"
          :disabled="isRedirecting"
          @click="handleGoToProfile"
        >
          your profile
        </button>. Keep it current if you change phones.
      </p>
      <V2MoleculesBanner
        v-else
        class="mb-6 w-full !p-4 text-left"
        type="info"
        content="<p class='text-semantic-sizing-body-sm'>Update your number anytime in your Profile. Keep it up to date if you change phones.</p>"
      />

      <V2AtomsButton
        id="mfa_enrollment_go_to_dashboard_btn"
        :class="ctaGroupClass"
        data-testid="mfa-enrollment-go-to-dashboard-btn"
        size="base"
        full
        :disabled="isRedirecting"
        enable-tracking
        @click="handleGoToDashboard"
      >
        Go to Dashboard
      </V2AtomsButton>
    </div>

    <div v-else-if="currentStep === STEP_CODE" :class="[stepFlowClass, bottomSpacingClass]">
      <!--
        The code's attempt budget is spent and it has been cancelled, so the input is
        replaced outright rather than left accepting guesses it can no longer satisfy.
      -->
      <V2MoleculesOtpSendBlocked
        v-if="attemptLockout.visible.value"
        :image="EXHAUSTED_PANEL_IMAGE"
        :title="EXHAUSTED_PANEL_TITLE"
        :description="EXHAUSTED_PANEL_DESCRIPTION"
        :destination="codeDestination"
        :seconds-remaining="attemptLockout.secondsRemaining.value"
        :can-retry="attemptLockout.canRetry.value"
        :show-not-now="false"
        data-testid="mfa-enrollment-otp-attempts-blocked"
        @retry="attemptLockout.retry(handleResend)"
      />
      <V2MoleculesOtpVerification
        v-else
        :pin-cta="!embedded"
        auto-submit-on-complete
        class="flex-1 md:flex-none"
        title="Enter your code"
        :destination="codeDestination"
        expiry-label="It expires in 5 minutes."
        :error="errorMessage"
        :is-submitting="isSubmitting"
        :show-cant-access="!isRecovery && !showPhoneInput"
        data-testid="mfa-enrollment-otp-verification"
        @verify="handleVerify"
        @resend="handleResend"
        @cant-access-click="handleCodeCantAccessClick"
      >
        <template #back>
          <button
            id="mfa_enrollment_code_back_btn"
            type="button"
            data-testid="mfa-enrollment-back-btn"
            class="mb-6 inline-flex items-center gap-1 text-sm font-semibold text-semantic-primary-default underline"
            @click="resetCodeStep"
          >
            <V2IconsArrow
              direction="left"
              fill="currentColor"
              width="16"
              height="16"
            />
            Back
          </button>
        </template>
      </V2MoleculesOtpVerification>

      <V2AtomsButton
        v-if="allowSkip"
        id="mfa_enrollment_code_not_now_btn"
        data-testid="mfa-enrollment-code-not-now-btn"
        enable-tracking
        type="button"
        size="small"
        theme="tertiary"
        class="mx-auto mt-4 block"
        :disabled="isSubmitting"
        @click="emits('skip')"
      >
        Not now
      </V2AtomsButton>
    </div>

    <div v-else :class="[stepFlowClass, bottomSpacingClass]">
      <button
        v-if="showBack"
        id="mfa_enrollment_phone_back_btn"
        type="button"
        data-testid="mfa-enrollment-phone-back-btn"
        class="mb-6 inline-flex items-center gap-1 text-sm font-semibold text-semantic-primary-default underline"
        :disabled="isSubmitting"
        @click="handleBackClick"
      >
        <V2IconsArrow
          direction="left"
          fill="currentColor"
          width="16"
          height="16"
        />
        Back
      </button>
      <!-- Recovery follows its own design: 40px to the title. -->
      <div
        class="flex justify-center"
        :class="isRecovery ? 'mb-10' : 'mb-6'">
        <img
          :src="phoneStepImage"
          alt=""
          width="160"
          height="160"
        />
      </div>
      <h1
        class="text-center text-semantic-surface-on-surface"
        :class="isRecovery
          ? 'mb-4 text-semantic-sizing-headline-sm font-semibold'
          : 'mb-2 text-lg font-bold leading-[150%]'">
        {{ phoneStepTitle }}
      </h1>
      <p
        class="text-center text-base font-normal leading-[150%] text-semantic-surface-on-surface"
        :class="isRecovery ? 'mb-6' : 'mb-4'">
        {{ phoneStepSubtitle }}
      </p>

      <form :class="stepFlowClass" @submit.prevent="handleSubmit">
        <!--
          Recovery's whole premise is that the number on file is unreachable — shown here
          only for context (never as a toggleable "send to this instead" option, unlike
          ordinary enrolment's known-number summary below). Struck through because it is
          being replaced, yet still where codes go until the new one is confirmed.
        -->
        <div
          v-if="isRecovery && hasUsableInitialPhone"
          data-testid="mfa-enrollment-current-number"
          class="mb-6 flex flex-col items-center gap-1 text-center">
          <span class="text-base font-semibold text-semantic-surface-on-surface">Current number</span>
          <span class="flex items-center gap-2 text-lg font-bold text-semantic-surface-on-surface line-through">
            <V2IconsPhoneOutgoingOutline
              width="20"
              height="20"
            />
            {{ currentNumberDisplay }}
          </span>
          <span class="text-sm font-normal leading-[150%] text-semantic-surface-on-surface-variant">
            Codes will keep coming here until your new number is confirmed.
          </span>
        </div>
        <template v-if="!showPhoneInput">
          <p class="mb-2 text-base font-normal leading-[150%] text-semantic-surface-on-surface">
            We'll send it to the mobile we have in your account:
          </p>
          <!--
            Side-by-side (justify-between) squeezed the phone number and this link into a
            single cramped row on mobile — stacked below the sm breakpoint, back to
            side-by-side once there's room for both.
          -->
          <div class="mb-4 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span class="flex items-center gap-2 font-bold text-semantic-surface-on-surface">
              <V2IconsPhoneOutgoingOutline
                width="16"
                height="16"
              />
              {{ maskedInitialPhone }}
            </span>
            <button
              id="mfa_enrollment_use_different_number_btn"
              type="button"
              data-testid="mfa-enrollment-use-different-number-btn"
              class="text-sm font-semibold text-semantic-primary-default underline"
              @click="switchToPhoneInput"
            >
              Not the right number? Use a different one
            </button>
          </div>
        </template>
        <!--
          Country picker + number. Replaces the free-text box and its "Include your country
          code" hint: the picker supplies the code, so the user only types the number.
          Emits E.164; the Member Hub copy is derived from it in enteredPhone().
        -->
        <V2MoleculesPhoneInput
          v-else
          id="mfa-enrollment-phone"
          v-model="phone"
          value-format="e164"
          name="phone"
          :label="isRecovery ? 'New mobile number' : 'Mobile number'"
          :error-message="fieldError"
          :disabled="isSubmitting"
          data-testid="mfa-enrollment-phone-input"
        />

        <V2MoleculesBanner
          data-testid="mfa-enrollment-info-banner"
          class="mb-10 mt-6 w-full !p-4"
          type="info"
          :content="infoBannerContent"
        />

        <div
          v-if="errorMessage"
          role="alert"
          data-testid="mfa-enrollment-error-alert"
          class="mb-4 w-full rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {{ errorMessage }}
        </div>

        <div class="grid gap-4 grid-cols-1" :class="ctaGroupClass">
          <V2AtomsButton
            id="mfa_enrollment_send_code_btn"
            data-testid="mfa-enrollment-send-code-btn"
            enable-tracking
            type="submit"
            size="base"
            full
            :disabled="isSubmitting"
          >
            Send Code
          </V2AtomsButton>
          <V2AtomsButton
            v-if="allowSkip"
            id="mfa_enrollment_not_now_btn"
            data-testid="mfa-enrollment-not-now-btn"
            enable-tracking
            type="button"
            size="small"
            theme="tertiary"
            :disabled="isSubmitting"
            @click="emits('skip')"
          >
            Not now
          </V2AtomsButton>
          <p
            v-if="allowSkip && !isRecovery"
            data-testid="mfa-enrollment-set-up-later-hint"
            class="-mt-2 text-center text-xs font-normal leading-[150%] text-semantic-surface-on-surface-variant"
          >
            You can set this up any time in your profile.
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { describePhoneError, formatPhone, isUsablePhone, parsePhone, toE164, withoutCountryCode } from '#shared/phone'
import { DEFAULT_PHONE_COUNTRY, PHONE_COUNTRIES } from '~/constants/country.constants'
import { DESCOPE_ERROR_INVALID_PHONE, DESCOPE_ERROR_MAX_OTP_ATTEMPTS, DESCOPE_ERROR_NETWORK, isOtpSendRateLimited, isWrongOtpCode } from '~/composables/use-descope-web-auth'
import useOtpAttempts, {
  ATTEMPT_LOCKOUT_SECONDS,
  EXHAUSTED_MESSAGE,
  EXHAUSTED_PANEL_DESCRIPTION,
  EXHAUSTED_PANEL_IMAGE,
  EXHAUSTED_PANEL_TITLE
} from '~/composables/use-otp-attempts'
import useOtpSendLockout from '~/composables/use-otp-send-lockout'
import { phoneNumberValidator } from '~/helpers/validator'
import useMfaLogging, { MFA_EVENT, MFA_CONTEXT, MFA_TRIGGER } from '~/composables/use-mfa-logging'

defineOptions({
  name: 'V2OrganismAuthMfaEnrollment'
})

const STEP_PHONE = 'phone'
const STEP_CODE = 'code'
const STEP_SUCCESS = 'success'

const props = defineProps({
  loginId: {
    type: String,
    required: true
  },
  refreshToken: {
    type: String,
    required: true
  },
  initialPhone: {
    type: String,
    default: ''
  },
  embedded: {
    type: Boolean,
    default: false
  },
  allowSkip: {
    type: Boolean,
    default: true
  },
  /*
   * Renders a Back control on the phone step. Opt-in because the parent owns where "back"
   * goes: the account-settings card returns to its summary, and the standalone setup page
   * abandons enrolment back to login. The auth modal offers "Not now" instead and has
   * nowhere to return to.
   */
  showBack: {
    type: Boolean,
    default: false
  },
  /*
   * 'enrollment' (default): the original "add two-step verification" copy/behavior,
   * unchanged. 'recovery': the lost-phone recovery flow's "update your mobile number" step
   * — same phone-capture/OTP/success mechanics, different copy, and authorizes the update
   * via startPhoneRecovery (using the caller's already-verified email-OTP refresh token)
   * instead of startSmsMfaSetup.
   */
  variant: {
    type: String,
    default: 'enrollment',
    validator: value => ['enrollment', 'recovery'].includes(value)
  },
  /*
   * Which surface mounted this component, for user-journey logging (auth_logs). The same
   * enrolment steps run from the standalone setup page, the legacy auth modal, the account
   * settings card and the lost-phone recovery flow — without this they are one
   * indistinguishable blob in the log. One of MFA_CONTEXT.
   */
  logContext: {
    type: String,
    default: MFA_CONTEXT.ENROLMENT
  },
  /*
   * Access token for the logging calls, for parents whose primary session is suspended
   * while this component is on screen (the setup page's mandatory-enrolment flow, the auth
   * modal, account recovery). Those have no token in storage for the api interceptor to
   * find, so it has to be passed in. Omit it when the session is live — account settings.
   * Used for logging only; never for Descope calls, which use `refreshToken`.
   */
  logAuthToken: {
    type: String,
    default: ''
  },
  dataTestid: {
    type: String,
    default: 'v2-organism-auth-mfa-enrollment'
  }
})
const emits = defineEmits(['completed', 'skip', 'phone-captured', 'back'])

const store = useUserStore()
const { $toast, $tracking } = useNuxtApp()
const { startSmsMfaSetup, startPhoneRecovery, verifySmsMfaSetup } = useDescopeWebAuth()
const { logMfaEvent } = useMfaLogging()
const otpAttempts = useOtpAttempts()
// Cooldown after burning a code's attempt budget — the same countdown/gate primitive the
// send-side lockout uses, armed from a different cause. See mfa-challenge for the pairing.
const attemptLockout = useOtpSendLockout()

/*
 * Journey logging for whichever surface mounted this component — the context and the token
 * both come from the parent, since this component cannot know which flow it is part of or
 * whether the primary session is currently suspended.
 */
function logEnrolmentEvent (event, detail = {}) {
  logMfaEvent(event, {
    context: props.logContext,
    token: props.logAuthToken || undefined,
    ...detail
  })
}

const isRecovery = computed(() => props.variant === 'recovery')

// Member Hub masks phone_number (e.g. "**********") on profiles it considers
// unverified — that's not a real, SMS-able number, so it must never be trusted as one:
// treat it the same as no phone on file and force manual entry instead of silently
// trying to text a masked placeholder.
// Prefill whenever the stored number is real and unmasked — see isUsablePhone.
const hasUsableInitialPhone = computed(() => isUsablePhone(props.initialPhone))

const currentStep = ref(STEP_PHONE)
// Recovery always shows the editable field: the whole point is that the number on file is
// unreachable, so there is nothing to "confirm and send to" the way ordinary enrolment does.
const showPhoneInput = ref(isRecovery.value || !hasUsableInitialPhone.value)
const phone = ref(!isRecovery.value && hasUsableInitialPhone.value ? props.initialPhone : '')
const maskedPhone = ref('')
const fieldError = ref('')
const errorMessage = ref('')
const isSubmitting = ref(false)
const isRedirecting = ref(false)
const completedPayload = ref(null)

// validatePhone and the Descope call only re-run on submit, so without this a stale error —
// either the local fieldError (e.g. "Please enter your mobile number.") or the Descope-side
// errorMessage alert from a prior failed send — keeps showing while the user is actively
// typing a now-valid number. It doesn't clear until they submit again.
watch(phone, () => {
  if (fieldError.value) fieldError.value = ''
  if (errorMessage.value) errorMessage.value = ''
})

/*
 * Full-page usage — the standalone setup page, which is what the mobile app renders —
 * drops the CTA group to the bottom of the screen on small viewports, per design. The
 * embedded usages (the account-settings card and the auth modal) sit inside other content,
 * so they keep the natural flow and are left untouched.
 *
 * mt-auto only below md: on a tall desktop viewport pinning the button to the bottom would
 * leave a large gap rather than reading as a footer. pb-safe resolves to 0 off-device.
 */
/*
 * Bottom-pinned CTAs are a MOBILE treatment. Every growth class here is undone from md up,
 * or the column keeps filling on desktop and pushes the CTAs to the foot of a tall viewport
 * — which is what happened to "Not now" on the code step.
 */
const pageFlowClass = computed(() => (props.embedded ? '' : 'flex h-full flex-col md:h-auto md:block'))
const stepFlowClass = computed(() => (props.embedded ? '' : 'flex flex-1 flex-col md:flex-none md:block'))

/* Positioning only — see bottomSpacingClass for why the inset is not applied here. */
const ctaGroupClass = computed(() => (props.embedded ? '' : 'mt-auto md:mt-0'))

/*
 * Breathing room under a bottom-pinned CTA, on the step container rather than the control.
 *
 * pb-safe used to sit on the CTA itself, which put the inset INSIDE the button and stretched
 * it. And pb-safe alone is env(safe-area-inset-bottom) with no fallback — 0 in an ordinary
 * mobile browser — so the CTA ended up flush against the bottom edge. calc() keeps a real
 * 1rem gap and adds the inset on devices that report one.
 */
const bottomSpacingClass = computed(
  () => (props.embedded ? '' : 'pb-[calc(1rem+env(safe-area-inset-bottom))] md:pb-0')
)

const maskedInitialPhone = computed(() => maskPhoneDisplay(props.initialPhone))
/*
 * Where the code step says the code went. Masking protects a number already on the
 * account; a number the user has just typed gains nothing from it (per design), so that
 * one is shown in full — AU in local format ("0499 766 560"), anything else in E.164.
 */
const codeDestination = computed(() => showPhoneInput.value
  ? formatEnteredPhoneDisplay(phone.value)
  : maskedPhone.value)
/*
 * Recovery's "Current number" — shown in full, per design: by this step the user has
 * already verified their email, and it is their own number being replaced.
 */
const currentNumberDisplay = computed(() => formatInternationalPhoneDisplay(props.initialPhone))
/*
 * Read from the entered number, not completedPayload.phone: the payload strips the dialling
 * code for Member Hub, so a +91 number used to be shown here re-grouped as if it were AU.
 */
const formattedCompletedPhone = computed(() => formatEnteredPhoneDisplay(phone.value))

const phoneStepTitle = computed(() => isRecovery.value
  ? 'Let\'s update your mobile number'
  : 'Add an extra layer of security')
const phoneStepSubtitle = computed(() => isRecovery.value
  ? 'We\'ll send a code to your new number to verify it.'
  : 'We\'re introducing two-step verification to better protect your account. You won\'t need a code every time, only when you log in.')
// Both variants share the info banner (on-surface copy); only the message differs.
const infoBannerContent = computed(() => `<p class='text-semantic-sizing-body-sm'>${isRecovery.value
  ? 'This step is required to keep your account secure.'
  : 'We\'ll use this number to send security codes, and to prefill forms so you don\'t retype it.'}</p>`)
const phoneStepImage = computed(() => isRecovery.value
  ? '/images/dashboard/vault/hand-phone.svg'
  : '/images/auth/mfa-enrollment.png')
const successBody = computed(() => isRecovery.value
  ? 'Your mobile number has been updated. We\'ll text you a code the next time you log in.'
  : 'Two-step verification is on. We\'ll text you a code when you log in.')

/*
 * Masks a phone number for display before any server-side masking is available, e.g.
 * "0499766560" -> "04•• ••• 560".
 */
function maskPhoneDisplay (value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length < 6) return value
  return `${digits.slice(0, 2)}•• ••• ${digits.slice(-3)}`
}

/*
 * Formats a 10-digit AU mobile number for display, e.g. "0499766560" -> "0499 766 560".
 */
function formatPhoneDisplay (value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length !== 10) return value || ''
  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
}

/*
 * A number the user entered, shown in full: AU in local format ("0499 766 560"), anything
 * else in E.164, since other countries' grouping varies.
 */
function formatEnteredPhoneDisplay (value) {
  const e164 = toE164(value)
  return /^\+61\d{9}$/.test(e164)
    ? formatPhoneDisplay(`0${e164.slice(3)}`)
    : formatInternationalPhoneDisplay(value)
}

/*
 * Formats a number with its dialling code, e.g. "0499766560" or "+61499766560" ->
 * "+61 499 766 560". Non-AU numbers are shown in E.164 as-is, since their grouping varies.
 */
function formatInternationalPhoneDisplay (value) {
  const e164 = toE164(value)
  if (/^\+61\d{9}$/.test(e164)) {
    return `+61 ${e164.slice(3, 6)} ${e164.slice(6, 9)} ${e164.slice(9)}`
  }
  return e164 || value || ''
}

/*
 * Plain <button>s bypass V2AtomsButton's enable-tracking, so they report the click
 * themselves. $tracking.click reads elementId off the DOM, which is why each carries an id.
 */
function trackClick (event) {
  // No event means the handler was invoked programmatically, not by a user click — there is
  // nothing to report, and $tracking.click reads the DOM event directly.
  if (!event) return

  // Optional: the tracking plugin is client-only and absent in some environments.
  $tracking?.click(event)
}

function handleBackClick (event) {
  trackClick(event)
  emits('back')
}

function switchToPhoneInput (event) {
  trackClick(event)
  showPhoneInput.value = true
  phone.value = ''
  fieldError.value = ''
}

function clearErrors () {
  fieldError.value = ''
  errorMessage.value = ''
}

/*
 * Descope puts its errorDescription (falling back to errorMessage) into error.message via
 * toAuthError, so the message shown inline is the same text Descope returned. Also raised
 * as a toast: the inline alert can sit off-screen once these forms fill the viewport on
 * mobile, and a failed send or verify otherwise looks like nothing happened.
 */
function reportDescopeError (message) {
  $toast.error(message)
  return message
}

/*
 * Every Descope send/verify error is mapped explicitly here rather than falling back to
 * error.message (Descope's own errorDescription/errorMessage) by default — that text is
 * written for API consumers, not end users: raw provider codes, upstream carrier jargon
 * ("ApiError 60200", "Status: 400"), or bare descriptions like "Failed to send sms" that
 * validatePhone() already failed to catch client-side (a structurally plausible number a
 * carrier or Descope itself still rejects). Anything not explicitly recognized below falls
 * to one generic, non-technical message instead of echoing whatever Descope returned.
 */
// 'send' (sendCode/handleResend) vs 'verify' (handleVerify) only changes the final,
// unrecognized-error fallback — every mapped case above it applies to both contexts.
function getErrorMessage (error, context = 'send') {
  // Descope's own text for this one is a generic "Request is missing required arguments" —
  // accurate to the API contract but meaningless to a user who just typed a bad number. A
  // format example gives them something actionable, without echoing what they actually typed.
  if (error?.code === DESCOPE_ERROR_INVALID_PHONE) {
    return 'Please enter a valid phone number in the format +61 412 345 678.'
  }

  // Confirmed with Descope support: distinct from a single wrong guess, which this code
  // never returns — Descope discards the code once its own attempt limit is hit, so the
  // fix is a new code, not "try again" on the one just rejected.
  if (error?.code === DESCOPE_ERROR_MAX_OTP_ATTEMPTS) {
    return EXHAUSTED_MESSAGE
  }

  /*
   * An ordinary wrong guess against a code that is still alive. Descope's own message
   * ("One time code is invalid") is clean but says nothing about how many tries are left,
   * so the local budget supplies that — handleVerify records the failure before calling
   * here, so wrongCodeMessage already reflects this attempt.
   */
  if (isWrongOtpCode(error)) {
    return otpAttempts.wrongCodeMessage.value
  }

  if (isOtpSendRateLimited(error)) {
    return 'Too many attempts. Please wait a moment before requesting another code.'
  }

  // Hand-written in use-descope-web-auth.js (see withDescopeLoader) for a raw network-level
  // failure — already user-safe, unlike every other error.message here.
  if (error?.code === DESCOPE_ERROR_NETWORK) {
    return error.message
  }

  /*
   * Verify-side errors (e.g. E061102 "One time code is invalid") have shown no evidence of
   * leaking technical/provider text the way sends have — pass Descope's own message
   * through here, same as every other MFA surface (mfa-challenge.vue, Authentication.vue),
   * rather than hiding a perfectly clean, already-actionable message behind a generic one.
   * Sends stay fully generic below: confirmed leaky ("Failed to send sms", raw Twilio
   * codes) with no reason yet found to trust an unmapped send error's text.
   */
  if (context === 'verify') {
    return error?.message || 'Something went wrong. Please try again.'
  }

  return 'Unable to send SMS. Please check your phone number and try again.'
}

function resetCodeStep (event) {
  trackClick(event)
  currentStep.value = STEP_PHONE
  maskedPhone.value = ''
  clearErrors()
}

/*
 * "Can't access this number?" on the known-number OTP step — the second recovery entry
 * point called for in the spec (agent-os/specs/2026-09-17-1500-mfa-lost-phone-recovery),
 * previously unreachable since show-cant-access was hardcoded off here. Unlike the
 * login-time SMS challenge, this step only ever runs on a full, live, already-authenticated
 * session — enrolment runs after login commits (see runMfaGate/proceedAfterFirstFactor) —
 * so there is no suspended session to hand into the lost-phone-recovery page. The safe fix
 * is simpler: drop back to the phone step with the editable field open, exactly what "Use a
 * different number" already does before a code is even sent.
 */
function handleCodeCantAccessClick (event) {
  trackClick(event)
  currentStep.value = STEP_PHONE
  maskedPhone.value = ''
  clearErrors()
  switchToPhoneInput()
}

/*
 * The editable field holds E.164 once the user has typed ("+61412345678"), or whatever the
 * caller prefilled. Splitting it again here — rather than asking the field — keeps the
 * values below correct even before the field has re-rendered from `phone`.
 */
const enteredParts = computed(() => parsePhone(phone.value, { countries: PHONE_COUNTRIES, defaultIso: DEFAULT_PHONE_COUNTRY }))

/*
 * What a send uses. Descope always gets E.164. Member Hub gets "local": "0412345678" for
 * AU (its stored format, and what withoutCountryCode() gave for a typed "04…") and the
 * national digits elsewhere — the same thing withoutCountryCode() produced for "+91…".
 *
 * The known-number path ("We'll send it to the mobile we have…") is unchanged: it never
 * shows the field, so the stored value still goes through toE164 / withoutCountryCode.
 */
function enteredPhone () {
  if (!showPhoneInput.value) {
    return { e164: toE164(phone.value), memberHub: withoutCountryCode(phone.value) }
  }
  return {
    e164: formatPhone(enteredParts.value, 'e164', PHONE_COUNTRIES),
    memberHub: formatPhone(enteredParts.value, 'local', PHONE_COUNTRIES)
  }
}

function validatePhone () {
  if (!showPhoneInput.value) return validateStoredPhone()

  // Same wording the field uses on blur (describePhoneError), so the two never disagree.
  fieldError.value = describePhoneError(enteredParts.value, PHONE_COUNTRIES, 'mobile')
  return !fieldError.value
}

/*
 * The stored number on the known-number path. Kept as it was: it predates the split field
 * and still has to judge a value Member Hub stored in any of its historical shapes.
 */
function validateStoredPhone () {
  if (!phone.value) {
    fieldError.value = 'Please enter your mobile number.'
    return false
  }

  /*
   * A phone number never contains letters — only digits plus the punctuation people type
   * around them ("+", spaces, dashes, parens). Anything else (pasted text, a mistyped
   * username, "njwdbklqjwbdfoi237ry23") is rejected right here rather than being partially
   * digit-stripped by toE164() and sent to Descope, which rejects it and echoes back its own
   * technical "Failed to send sms" / provider error text — meaningless to the user and not
   * what they typed. This also covers the pure-letters case ("askjdhasdaskjdhk"), which
   * strips to zero digits.
   */
  if (!/^[\d\s+\-()]+$/.test(phone.value.trim())) {
    fieldError.value = 'Please enter a valid mobile number.'
    return false
  }

  /*
   * Local AU format ("04...") has one exact, well-known shape. Catching a wrong digit
   * count here — rather than letting toE164() fall through to its last-resort `+${digits}`
   * — stops a malformed number (e.g. an extra typed digit) from ever reaching Descope,
   * which otherwise rejects it and echoes that mangled value straight back to the user
   * ("+04999875469" is not a valid phone number") — confusing, since it's not what they
   * typed. A number already carrying its own "+" is left to Descope's own validation,
   * since this app also supports non-AU numbers (see the COUNTRY_CALLING_CODES table in
   * shared/phone.ts) that this local-format check has no business rejecting.
   */
  const digits = phone.value.trim().replace(/\D/g, '')

  // Passed the character check above (only punctuation, e.g. "+()---") but still has no
  // actual digits.
  if (!digits) {
    fieldError.value = 'Please enter a valid mobile number.'
    return false
  }

  /*
   * QA repro (CLUB-786): a 10-digit number typed WITHOUT the leading "0" (e.g.
   * "9984522333") starts with neither "0" nor "+", so the check above never ran at all —
   * not "wrong shape", just entirely skipped. It reached toE164()'s last-resort
   * `+${digits}` fallback as "+9984522333", which Twilio then rejected with its own
   * technical error. Any non-"+" input is now checked against BOTH plausible local shapes
   * — "0" + 9 digits, or the same 9 digits with the leading "0" simply omitted — so a
   * missing trunk zero is caught here instead of reaching Descope at all.
   */
  if (!phone.value.trim().startsWith('+')) {
    const isValidAuShape = digits.startsWith('0')
      ? phoneNumberValidator(digits)
      : phoneNumberValidator(digits, true)
    if (!isValidAuShape) {
      fieldError.value = 'Please enter a valid 10-digit mobile number starting with 0 (e.g. 0412 345 678), or include your country code (e.g. +61 412 345 678).'
      return false
    }
  }

  fieldError.value = ''
  return true
}

async function handleSubmit () {
  clearErrors()
  await sendCode()
}

async function sendCode (trigger = MFA_TRIGGER.AUTO) {
  if (isSubmitting.value) return

  if (!validatePhone()) {
    /*
     * The "known number" step (showPhoneInput false) has nowhere to render fieldError —
     * only the editable field below does. Without this, a stored number that fails local
     * validation (e.g. missing its trunk "0") left "Send Code" silently doing nothing, with
     * no visible error at all. Switching to the editable field surfaces both the number and
     * why it was rejected, and lets the user fix it in place.
     */
    showPhoneInput.value = true
    // Now on the editable field: re-word the error the way the field itself would.
    fieldError.value = describePhoneError(enteredParts.value, PHONE_COUNTRIES, 'mobile') || fieldError.value
    return
  }

  const { e164, memberHub } = enteredPhone()
  isSubmitting.value = true
  try {
    const result = isRecovery.value
      ? await startPhoneRecovery(props.loginId, e164, props.refreshToken)
      : await startSmsMfaSetup(props.loginId, e164, props.refreshToken)
    maskedPhone.value = result.maskedPhone
    currentStep.value = STEP_CODE
    // Each code carries its own attempt budget — a newly sent one starts full. Only on
    // success: a failed send leaves the previous code, and its part-spent budget, in play.
    otpAttempts.reset()
    attemptLockout.reset()
    logEnrolmentEvent(MFA_EVENT.CODE_SENT, { trigger })
    // Member Hub holds numbers without a dialling code; only Descope wants E.164.
    emits('phone-captured', memberHub)
  } catch (error) {
    logEnrolmentEvent(
      isOtpSendRateLimited(error) ? MFA_EVENT.SEND_RATE_LIMITED : MFA_EVENT.CODE_SEND_FAILED,
      { trigger, failure_reason: error?.code || 'unknown' }
    )
    errorMessage.value = reportDescopeError(getErrorMessage(error))
  } finally {
    isSubmitting.value = false
  }
}

async function handleResend () {
  // The rejected code is gone from the field, so its error must go with it.
  clearErrors()

  try {
    const { e164 } = enteredPhone()
    if (isRecovery.value) {
      await startPhoneRecovery(props.loginId, e164, props.refreshToken)
    } else {
      await startSmsMfaSetup(props.loginId, e164, props.refreshToken)
    }
    otpAttempts.reset()
    attemptLockout.reset()
    logEnrolmentEvent(MFA_EVENT.CODE_SENT, { trigger: MFA_TRIGGER.RESEND })
  } catch (error) {
    logEnrolmentEvent(
      isOtpSendRateLimited(error) ? MFA_EVENT.SEND_RATE_LIMITED : MFA_EVENT.CODE_SEND_FAILED,
      { trigger: MFA_TRIGGER.RESEND, failure_reason: error?.code || 'unknown' }
    )
    errorMessage.value = reportDescopeError(getErrorMessage(error))
  }
}

async function handleVerify (otpValue) {
  if (isSubmitting.value) return

  /*
   * The code's attempt budget is already spent — refuse locally instead of sending another
   * doomed verify. See useOtpAttempts.isExhausted for why the UI has to enforce this at all
   * (Descope currently keeps accepting guesses). Logged as its own failure_reason so the
   * audit trail distinguishes a lockout we applied from one Descope reported.
   */
  if (otpAttempts.isExhausted.value) {
    logEnrolmentEvent(MFA_EVENT.MAX_ATTEMPTS_EXCEEDED, { failure_reason: 'max_attempts_local' })
    errorMessage.value = reportDescopeError(EXHAUSTED_MESSAGE)
    return
  }

  isSubmitting.value = true
  errorMessage.value = ''
  try {
    const { token } = await verifySmsMfaSetup(props.loginId, otpValue)
    logEnrolmentEvent(MFA_EVENT.CHALLENGE_SUCCEEDED)
    const payload = { token, phone: enteredPhone().memberHub }
    // Embedded usages (e.g. account settings) live inline in a small card — there's
    // nowhere to "go to dashboard" from there, so skip the full-page success screen
    // and hand control straight back to the parent, exactly like the pre-redesign flow.
    if (props.embedded) {
      emits('completed', payload)
      return
    }
    completedPayload.value = payload
    currentStep.value = STEP_SUCCESS
  } catch (error) {
    // Same max-attempts signal the login challenge uses: this code is the only way to tell
    // "the code is now dead, request a new one" from an ordinary wrong guess.
    const isMaxAttempts = error?.code === DESCOPE_ERROR_MAX_OTP_ATTEMPTS
    // Before getErrorMessage below, which reads the updated count for its wrong-code copy.
    if (isWrongOtpCode(error)) otpAttempts.recordFailure()
    const isLocallyExhausted = !isMaxAttempts && otpAttempts.isExhausted.value

    // Budget gone — swap the input out for the lockout panel. Armed on Descope's own
    // max-attempts error too, so both routes to a dead code land on the same screen.
    if (isMaxAttempts || isLocallyExhausted) {
      attemptLockout.start(ATTEMPT_LOCKOUT_SECONDS)
    }
    // The guess that spends the local budget is the one that raises the lockout panel, so
    // it logs as max attempts — not as one more ordinary wrong code.
    logEnrolmentEvent(isMaxAttempts || isLocallyExhausted ? MFA_EVENT.MAX_ATTEMPTS_EXCEEDED : MFA_EVENT.CHALLENGE_FAILED, {
      failure_reason: isMaxAttempts
        ? 'max_attempts'
        : (isLocallyExhausted ? 'max_attempts_local' : (error?.code === DESCOPE_ERROR_NETWORK ? 'network' : 'invalid_code'))
    })
    errorMessage.value = reportDescopeError(getErrorMessage(error, 'verify'))
  } finally {
    isSubmitting.value = false
  }
}

/*
 * Manual continue from the success screen — keeps the confirmation visible until the
 * user chooses to proceed, showing the app's global loader while the parent's
 * `completed` handler finishes signing them into the dashboard.
 */
function handleGoToDashboard () {
  finishEnrolment()
}

/*
 * The "your profile" link used to be a plain nuxt-link, which navigated away WITHOUT
 * emitting `completed` — so the parent never ran its completion handler and none of the
 * local state was written: no Descope side-session, no descope block on the profile, no
 * SMS preference. The factor was enrolled at Descope, but account settings then reported
 * the phone unverified and two-step off. It now completes first and carries its own
 * destination, so the parent lands the user there instead of the dashboard.
 */
function handleGoToProfile (event) {
  trackClick(event)
  finishEnrolment('/dashboard/account-settings/')
}

function finishEnrolment (destination) {
  if (isRedirecting.value || !completedPayload.value) return

  isRedirecting.value = true
  store.loader(true)
  emits('completed', { ...completedPayload.value, destination })
}

/*
 * The loader raised above is cleared here rather than by each parent, because it must be
 * held across whatever the `completed` handler does and only released once this form is
 * actually gone — which is the one moment every parent has in common, whether it navigates
 * to the dashboard or just swaps this step out for a summary.
 *
 * Without this it leaked: the setup page only cleared it as a side effect of
 * saveUserProfileDetails, so a user whose number was already on their profile skipped that
 * call and landed on the dashboard behind a loader that never went away.
 *
 * Guarded on isRedirecting so an unmount for any other reason cannot clear a loader this
 * component did not raise.
 */
onBeforeUnmount(() => {
  if (isRedirecting.value) store.loader(false)
})

/*
 * The enrolment steps swap in place — the route never changes, so app.vue's fullPath
 * watcher never fires a pageview for them. Report each one explicitly, keeping the real
 * path and distinguishing the step by title, so no synthetic URLs enter page reports.
 * Recovery gets its own titles so it's distinguishable from ordinary enrolment in analytics.
 */
const STEP_TITLES = computed(() => (isRecovery.value
  ? {
    [STEP_PHONE]: 'Account Recovery — Update mobile number',
    [STEP_CODE]: 'Account Recovery — Enter verification code',
    [STEP_SUCCESS]: 'Account Recovery — Mobile number updated'
  }
  : {
    [STEP_PHONE]: 'MFA — Add mobile number',
    [STEP_CODE]: 'MFA — Enter verification code',
    [STEP_SUCCESS]: 'MFA — Verification enabled'
  }))

watch(currentStep, step => {
  const title = STEP_TITLES.value[step]
  // Optional: $tracking comes from a client-only plugin that is inactive in tests and can
  // be absent if analytics fail to load. Reporting a step must never break the flow.
  if (title) $tracking?.pageview({ title })
}, { immediate: true })

/*
 * Completion signal for the lost-phone recovery flow specifically — separate from the
 * pageview above, so this shows up as its own event in GTM/analytics rather than only being
 * inferable from a page title. No PII: no email/phone, per tracking/tracking.md.
 */
watch(currentStep, step => {
  if (isRecovery.value && step === STEP_SUCCESS) {
    $tracking?.customEvent('mfa_phone_recovered_via_email', {})
  }
})

/*
 * The enrolment surface is now on screen. Fired on mount rather than on the first send, so
 * the funnel shows people who reached the step and never sent a code at all — the drop-off
 * this whole feature is meant to surface.
 */
onMounted(() => {
  // Not for recovery: reaching its phone stage is already mfa_recovery_email_verified, and
  // logging it as an enrolment start would count recoveries into the enrolment funnel.
  if (isRecovery.value) return
  logEnrolmentEvent(MFA_EVENT.ENROLMENT_STARTED)
})

watch(() => props.initialPhone, value => {
  if (!isUsablePhone(value)) return
  // Recovery's whole premise is that the number on file is unreachable — never prefill it
  // as the new number, and never toggle away from the editable field.
  if (isRecovery.value) return

  // Prefill the editable field from whatever arrived, and switch to the masked "we'll
  // send it to the mobile on file" display now that a usable number exists.
  if (!phone.value) {
    phone.value = value
  }

  showPhoneInput.value = false
})
</script>
